import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useQueryClient } from 'react-query';
import { IDailySchedule, ISchoolShiftBaseProps, ISchoolShiftFilterParams, ISchoolShiftSimpleProps, ITimeSlot, Weekday } from 'services/SchoolShift/types';
import { IPaginationParams } from 'services/types';
import { AppContext } from '../../../context/AppContextProvider';
import { useGetAllControllersByLicenseAsync } from '../../../services/Devices/hooks/useMutation';
import { IDeviceSimpleProps } from '../../../services/Devices/types';
import {
    useCreateSchoolShiftAsync,
    useDeleteSchoolShiftAsync,
    useUpdateSchoolShiftAsync,
} from '../../../services/SchoolShift/hooks/useMutation';
import {
    SCHOOL_SHIFT_KEYS,
    useGetAllSchoolShiftByLicenseQuery,
    useGetSchoolShiftByIdQuery,
    useGetSchoolShiftCountQuery
} from '../../../services/SchoolShift/hooks/useQuery';
import { SchoolShiftFormInput, SchoolShiftFormSchema } from '../../../services/SchoolShift/schemas';
import { DAYS_OF_WEEK } from '../_components/Constants';

export const useSchoolShift = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useContext(AppContext);
  const { t } = useTranslation();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState<IPaginationParams>({ pageNumber: 1, pageSize: 10 });
  const [filter, setFilter] = useState<ISchoolShiftFilterParams>({});
  
  const [selectedId, setSelectedId] = useState<number | 'new' | null>(null);
  const [devices, setDevices] = useState<IDeviceSimpleProps[]>([]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState<ISchoolShiftSimpleProps | null>(null);

  const buildWeeklySchedule = (sourceSchedule?: IDailySchedule[]) => {
    return DAYS_OF_WEEK.map((d) => {
      const backendDay = sourceSchedule?.find((ws: IDailySchedule) => ws.weekday === d.id);

      return {
        weekday: d.id as Weekday,
        dayName: t(d.labelKey),
        active: !!backendDay,
        timeSlots: backendDay?.timeSlots?.map((ts: ITimeSlot) => ({
          ...ts,
          start: ts.start ? ts.start.substring(0, 5) : '',
          end: ts.end ? ts.end.substring(0, 5) : '',
          isEnter: ts.isEnter || false,
          isExit: ts.isExit || false
        })) || []
      };
    });
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilter((prev) => ({ ...prev, name: searchTerm || undefined }));
      setPagination((prev) => ({ ...prev, pageNumber: 1 }));
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const { data: shiftList = [], isLoading: isLoadingList, isFetching: isFetchingList, refetch } = useGetAllSchoolShiftByLicenseQuery(pagination, filter);
  const { data: totalCount = 0, refetch: refetchCount } = useGetSchoolShiftCountQuery(filter);
  const { data: shiftData, isFetching: isFetchingDetail } = useGetSchoolShiftByIdQuery(
    typeof selectedId === 'number' ? selectedId : null
  );

  const { mutate: createShift, isLoading: isCreating } = useCreateSchoolShiftAsync();
  const { mutate: updateShift, isLoading: isUpdating } = useUpdateSchoolShiftAsync();
  const { mutateAsync: deleteShift, isLoading: isDeleting } = useDeleteSchoolShiftAsync();
  const { mutateAsync: fetchDevices } = useGetAllControllersByLicenseAsync();

  const methods = useForm<SchoolShiftFormInput>({
    resolver: zodResolver(SchoolShiftFormSchema),
    mode: 'onTouched',
    defaultValues: {
      name: '',
      deviceIds: [],
      weeklySchedule: buildWeeklySchedule()
    }
  });

  const { reset } = methods;

  useEffect(() => {
    fetchDevices({})
      .then(setDevices)
      .catch((err: any) => {
        showSnackbar(
          err.message || 'Erro ao carregar a lista de dispositivos disponíveis.', 
          'error'
        );
      });
  }, []);

  useEffect(() => {
    if (shiftData && typeof selectedId === 'number') {
      reset({ name: shiftData.name, deviceIds: shiftData.deviceIds || [], weeklySchedule: buildWeeklySchedule(shiftData.weeklySchedule) });
    }
  }, [shiftData, selectedId, reset, t]);

  const handleSelectShift = (id: number) => setSelectedId(id);

  const handleCreateNew = () => {
    setSelectedId('new');
    reset({
      name: '', deviceIds: [],
      weeklySchedule: buildWeeklySchedule()
    });
  };

  const handleOpenDeleteModal = (e: React.MouseEvent, shift: ISchoolShiftSimpleProps) => {
    e.stopPropagation();
    setShiftToDelete(shift);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!shiftToDelete) return;
    
    const idToDelete = shiftToDelete.id;

    try {
      await deleteShift(idToDelete);
      showSnackbar('Turno excluído com sucesso.', 'success');

      if (selectedId === idToDelete) {
        queryClient.removeQueries([SCHOOL_SHIFT_KEYS.ALL, SCHOOL_SHIFT_KEYS.DETAIL, idToDelete]);
        setSelectedId(null); 
      }

      if (shiftList.length === 1 && pagination.pageNumber > 1) {
        setPagination(prev => ({ ...prev, pageNumber: prev.pageNumber - 1 }));
      }

    } catch (err: any) {
      showSnackbar(err.message || 'Erro ao excluir turno.', 'error');
    } finally {
      setDeleteModalOpen(false);
      setShiftToDelete(null);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, pageNumber: newPage })); 
  };

  const handleRefresh = () => {
    refetch();
    refetchCount();
  };

  const onSave = (formData: SchoolShiftFormInput) => {
    const payload: ISchoolShiftBaseProps = {
      name: formData.name,
      deviceIds: formData.deviceIds,
      weeklySchedule: formData.weeklySchedule
        .filter(day => day.active)
        .map(day => ({
          weekday: day.weekday as Weekday,
          timeSlots: day.timeSlots.map(slot => ({
               start: slot.start.length === 5 ? `${slot.start}:00` : slot.start,
               end: slot.end.length === 5 ? `${slot.end}:00` : slot.end,
               isEnter: (!slot.isEnter && !slot.isExit) ? true : slot.isEnter,
               isExit: (!slot.isEnter && !slot.isExit) ? true : slot.isExit
          }))
        }))
    };

    if (selectedId === 'new') {
      createShift(
        { data: payload }, 
        { 
          onSuccess: (newShift) => { 
            showSnackbar('Turno cadastrado com sucesso!', 'success');
            setSelectedId(newShift.id); 
          }, 
          onError: (err: any) => {
            showSnackbar((err as Error).message || 'Erro ao criar turno.', 'error');
          }
        }
      );
    } else {
      updateShift(
        { id: Number(selectedId), data: payload }, 
        { 
          onSuccess: () => {
            showSnackbar('Alterações salvas com sucesso!', 'success');
          }, 
          onError: (err: any) => {
            showSnackbar((err as Error).message || 'Erro ao atualizar turno.', 'error');
          }
        }
      );
    }
  };

  return {
    state: {
      searchTerm, selectedId, devices, deleteModalOpen, shiftToDelete, 
      isLoadingList, isFetchingList, isFetchingDetail, isCreating, isUpdating, isDeleting,
      shiftList, totalCount, pagination
    },
    actions: {
      setSearchTerm, handleSelectShift, handleCreateNew, handleOpenDeleteModal, 
      handleConfirmDelete, setDeleteModalOpen, onSave,
      handleChangePage, handleRefresh
    },
    form: methods
  };
};